import { useState } from 'react';

import {
  Add,
  DataImportHub,
  EventSchedule,
  Maximize,
  Minimize,
  MintIqLogoHorizontal,
  Search,
} from '@mint-lib/icons';
import { Button } from '@mint-lib/tw-components';
import { cn } from '@mint-lib/tw-utils';

import { ChatList } from '../../components/chat-list/ChatList';
import { ChatSidebarToolbar } from '../../components/ChatSidebarToolbar';
import { useAiHubPermissions } from '../../hooks/useAiHubPermissions.jsx';
import { useChatSidebarState } from '../../hooks/useChatSidebarState.js';
import { useKnowledgeBase } from '../../hooks/useKnowledgeBase.js';
import { useScheduleDrawer } from '../../hooks/useScheduleDrawer.js';
import { RagDrawer } from '../KnowledgeBase/RagDrawer.jsx';
import { ScheduleDrawer } from '../ScheduleChat/ScheduleDrawer/index.js';

type Props = {
  isNewChat: boolean;
  onClickChatItem: (chatId: string) => void;
  onStartNewChat: () => void;
  onDeleteChat: () => void;
  isExpanded: boolean;
  onToggleExpanded: () => void;
  expandWidth?: number;
  chatId?: string;
  className?: string;
  withLogo?: boolean;
};

export const ChatSidebar = ({
  onStartNewChat,
  expandWidth = 300,
  className,
  isNewChat,
  isExpanded,
  onToggleExpanded,
  onClickChatItem,
  onDeleteChat,
  chatId,
  withLogo = false,
}: Props) => {
  const { canScheduleChat } = useAiHubPermissions();

  const {
    selectedChatType,
    setSelectedChatType,
    searchValue,
    debouncedSearchValue,
    handleChangeSearch,
    handleClearSearch,
  } = useChatSidebarState();

  const { toggle: toggleKB } = useKnowledgeBase();
  const { toggle: toggleScheduleList } = useScheduleDrawer();

  const [shouldAutoFocusSearch, setShouldAutoFocusSearch] = useState(false);

  const handleToggleExpanded = () => {
    setShouldAutoFocusSearch(false);
    onToggleExpanded();
  };

  const handleSearchExpand = () => {
    setShouldAutoFocusSearch(true);
    onToggleExpanded();
  };

  return (
    <div
      style={{ width: isExpanded ? `${expandWidth}px` : '64px' }}
      className={cn(
        'flex h-full shrink-0 [scrollbar-width:none] flex-col items-center justify-start overflow-y-auto rounded-lg border border-solid border-secondary-light bg-ui-primary p-3 pt-0 transition-[width] duration-200 ease-in-out',
        className,
      )}
      aria-label={
        isExpanded ? 'agentic-expander-expanded' : 'agentic-expander-compact'
      }
    >
      <div className="sticky top-0 z-sticky mb-2 flex w-full flex-col items-center justify-center gap-3 bg-ui-primary">
        <Header
          isExpanded={isExpanded}
          withLogo={withLogo}
          onToggle={handleToggleExpanded}
        />
        {isExpanded && (
          <ChatSidebarToolbar
            searchValue={searchValue}
            onChangeSearch={handleChangeSearch}
            onClearSearch={handleClearSearch}
            isNewChat={isNewChat}
            onStartNewChat={onStartNewChat}
            selectedChatType={selectedChatType}
            onChangeChatType={setSelectedChatType}
            onToggleKnowledgeBase={toggleKB}
            onToggleScheduleList={toggleScheduleList}
            autoFocusSearch={shouldAutoFocusSearch}
          />
        )}
      </div>
      <div className="flex h-full w-full flex-col">
        {isExpanded ? (
          <ChatList
            chatId={chatId}
            handleClickItem={onClickChatItem}
            onDeleteChat={onDeleteChat}
            chatType={selectedChatType}
            searchValue={debouncedSearchValue}
          />
        ) : (
          <div className="flex w-full flex-col items-center gap-3">
            <Button
              icon
              size="large"
              variant="text"
              onClick={handleSearchExpand}
              aria-label="Search chats"
            >
              <Search />
            </Button>
            <AddChatButton isDisabled={isNewChat} onClick={onStartNewChat} />
            <Button
              icon
              variant="text"
              onClick={toggleKB}
              aria-label="Knowledge base"
            >
              <DataImportHub />
            </Button>
            <Button
              icon
              variant="text"
              onClick={toggleScheduleList}
              disabled={!canScheduleChat}
              aria-label="Scheduled chat"
            >
              <EventSchedule />
            </Button>
          </div>
        )}
      </div>
      <ScheduleDrawer />
      <RagDrawer />
    </div>
  );
};

type HeaderProps = {
  isExpanded: boolean;
  withLogo: boolean;
  onToggle: () => void;
};

const Header = ({ isExpanded, withLogo, onToggle }: HeaderProps) => (
  <div
    className={cn(
      'flex h-15 w-full items-center justify-center',
      isExpanded ? 'flex-row' : 'flex-col',
    )}
  >
    {isExpanded && withLogo && (
      <MintIqLogoHorizontal
        className="h-6 w-59 fill-icon-primary"
        aria-label="mint-iq-logo-horizontal"
      />
    )}
    <div
      className={cn(
        'flex h-15 w-full flex-row items-center justify-center',
        isExpanded ? 'justify-end' : 'justify-center',
      )}
    >
      <Button
        icon
        size="large"
        variant="text"
        onClick={onToggle}
        aria-label="agentic-expander-button"
      >
        {isExpanded ? <Minimize /> : <Maximize />}
      </Button>
    </div>
  </div>
);

type AddChatButtonProps = {
  isDisabled: boolean;
  onClick: () => void;
};

const AddChatButton = ({ isDisabled, onClick }: AddChatButtonProps) => (
  <div className="flex w-full flex-col items-center whitespace-nowrap">
    <Button
      icon
      size="large"
      variant="contained"
      color="primary"
      disabled={isDisabled}
      onClick={onClick}
      aria-label="Add new chat"
    >
      <Add />
    </Button>
  </div>
);
